// src/services/authService.js
import apiClient from './apiClient';
import { TOKEN_KEY, ADMIN_TOKEN_KEY, USER_KEY, ADMIN_USER_KEY } from '../constants/storageKeys';
import { 
  clearAllAuthTokens, 
  storeUserData, 
  storeAdminUserData,
  getStoredUser,
  getStoredAdminUser
} from '../utils/tokenUtils';

export const authService = {
  // User authentication
  login: (email, password) => 
    apiClient.post('/auth/login', { email, password }),

  // Admin authentication
  adminLogin: (email, password) => 
    apiClient.post('/admin-auth/login', { email, password }),

  // Get current user profile
  getUserProfile: () => 
    apiClient.get('/user/profile'),

  // Get current admin profile
  getAdminProfile: () => 
    apiClient.get('/admin/profile'),

  // Store user token
  saveUserToken: (token) => {
    localStorage.setItem(TOKEN_KEY, token);
  },

  // Store admin token
  saveAdminToken: (token) => {
    localStorage.setItem(ADMIN_TOKEN_KEY, token);
  },

  // Get user token
  getUserToken: () => localStorage.getItem(TOKEN_KEY),

  // Get admin token
  getAdminToken: () => localStorage.getItem(ADMIN_TOKEN_KEY),

  // Check if user is logged in
  isUserLoggedIn: () => !!localStorage.getItem(TOKEN_KEY),

  // Check if admin is logged in
  isAdminLoggedIn: () => !!localStorage.getItem(ADMIN_TOKEN_KEY),

  // Store user data in a secure way (only id and essential info)
  storeUser: (userData) => {
    const safeUserData = {
      id: userData.id || userData._id,
      email: userData.email,
      name: userData.name,
      role: userData.role
    };
    storeUserData(safeUserData);
    return safeUserData;
  },

  // Store admin user data in a secure way (only essential info)
  storeAdminUser: (adminData) => {
    const safeAdminData = {
      id: adminData.id || adminData._id,
      userId: adminData.userId,
      email: adminData.email,
      name: adminData.name,
      role: adminData.role,
      permissions: adminData.permissions || []
    };
    storeAdminUserData(safeAdminData);
    return safeAdminData;
  },

  // Get stored user
  getUser: () => getStoredUser(),

  // Get stored admin user
  getAdminUser: () => getStoredAdminUser(),

  // Logout both user and admin
  logout: () => {
    clearAllAuthTokens();
  },

  // User specific logout
  userLogout: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },

  // Admin specific logout
  adminLogout: () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_USER_KEY);
  },

  // Request password reset (for both user and admin)
  requestPasswordReset: (email, isAdmin = false) => {
    const endpoint = isAdmin ? '/admin-auth/request-password-reset' : '/auth/request-password-reset';
    return apiClient.post(endpoint, { email });
  },

  // Reset password with token (for both user and admin)
  resetPassword: (token, password, isAdmin = false) => {
    const endpoint = isAdmin ? '/admin-auth/reset-password' : '/auth/reset-password';
    return apiClient.post(endpoint, { token, password });
  },

  // Change password (authenticated user or admin)
  changePassword: (currentPassword, newPassword, isAdmin = false) => {
    const endpoint = isAdmin ? '/admin-auth/change-password' : '/auth/change-password';
    return apiClient.post(endpoint, { currentPassword, newPassword });
  }
};

// Setup axios interceptors to attach authentication tokens to requests
apiClient.interceptors.request.use(config => {
  const userToken = localStorage.getItem(TOKEN_KEY);
  const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
  
  console.log('[Auth Interceptor] Tokens:', { 
    hasUserToken: !!userToken, 
    hasAdminToken: !!adminToken,
    url: config.url
  });

  // For admin-auth routes (login, etc), don't need tokens
  if (config.url.includes('/admin-auth')) {
    return config;
  }
  
  // For all other routes, prefer admin token (since we're in admin panel)
  // Especially for orgs and users endpoints which require admin auth
  if (adminToken) {
    config.headers.Authorization = `Bearer ${adminToken}`;
  } else if (userToken) {
    // Fallback to user token for some routes that might work with either token
    config.headers.Authorization = `Bearer ${userToken}`;
  }

  return config;
});

export default authService;